import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { buildScenario } from './cementing-status-fixtures';

export { buildScenario, CEMENTING_MOCK_SCENARIOS } from './cementing-status-fixtures';

/**
 * Dev-only mock of the Scholaranium cementing API, for viewing the cementing
 * panel locally in every state of the mockup.
 *
 * Off unless the app runs in a non-production build on a developer host
 * (localhost, 127.0.0.1 or *.local / *.localhost) AND a scenario is selected:
 *
 *     http://localhost:4200/journey/page/critical_reasoning?cementingMock=threshold-q4
 *
 * The query parameter is remembered for the tab (sessionStorage), so in-app
 * navigation keeps the scenario; `?cementingMock=off` clears it and returns to
 * the real API. `localStorage.cementingMock` still works as a sticky fallback.
 * A choice posted to the mock answers with the scenario that follows it.
 *
 * Scenarios: see CEMENTING_MOCK_SCENARIOS in cementing-status-fixtures.ts.
 */
@Injectable()
export class CementingStatusMockInterceptor implements HttpInterceptor {
  static readonly STORAGE_KEY = 'cementingMock';
  static readonly OFF_VALUES = ['off', 'real', 'false', '0', ''];

  /** Where a STOP on the waiting moment of a scenario leads. */
  private static readonly AFTER_STOP: { [scenario: string]: string } = {
    'threshold-q4': 'done-threshold-q4',
    'checkin-mastery-q6': 'done-threshold-q6',
    'with-retake': 'done-threshold-q6',
    'checkin-below-q5': 'done-not-met-q5'
  };

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const scenario = this.scenario();
    if (!scenario) { return next.handle(req); }

    const status = req.method === 'GET' && req.url.match(/\/cementing\/([^\/]+)\/status$/);
    if (status) {
      const body = buildScenario(scenario, decodeURIComponent(status[1]).toUpperCase());
      return of(new HttpResponse({ status: 200, body })).pipe(delay(150));
    }
    const choice = req.method === 'POST' && req.url.match(/\/cementing\/module\/([^\/]+)\/choice$/);
    if (choice) {
      const stop = req.body && String(req.body.choice).toUpperCase() === 'STOP';
      const nextScenario = stop ? (CementingStatusMockInterceptor.AFTER_STOP[scenario] || scenario) : scenario;
      this.remember(nextScenario);
      const body = buildScenario(nextScenario, 'CR');
      return of(new HttpResponse({ status: 200, body })).pipe(delay(250));
    }
    return next.handle(req);
  }

  /**
   * Scenario selection, in priority order: `?cementingMock=` in the current URL
   * (persisted to sessionStorage for the tab), then sessionStorage, then localStorage.
   */
  private scenario(): string | null {
    if (environment.production || !this.isLocalHost()) { return null; }
    const key = CementingStatusMockInterceptor.STORAGE_KEY;
    try {
      const fromUrl = new URLSearchParams(window.location.search).get(key);
      if (fromUrl !== null) {
        const value = fromUrl.trim();
        if (CementingStatusMockInterceptor.OFF_VALUES.indexOf(value.toLowerCase()) >= 0) {
          sessionStorage.removeItem(key);
          localStorage.removeItem(key);
          return null;
        }
        sessionStorage.setItem(key, value);
        return value;
      }
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  private remember(scenario: string) {
    try { sessionStorage.setItem(CementingStatusMockInterceptor.STORAGE_KEY, scenario); } catch (e) { /* storage blocked */ }
  }

  /** Only developer hosts: localhost, 127.0.0.1 or anything under .local / .localhost (e.g. lms.localhost). */
  private isLocalHost(): boolean {
    const host = (typeof window !== 'undefined' && window.location && window.location.hostname || '').toLowerCase();
    return host === 'localhost' || host === '127.0.0.1' || /\.local(host)?$/.test(host);
  }
}
